import type { MonthlyReportResponse } from "@central-hub/contracts";
import { useEffect, useState } from "react";
import { ApiError, api } from "./api";
import type { Session } from "./auth";
import { dayClock } from "./format";
import { Icon } from "./icons";
import { detailHref } from "./links";
import { monthLabel, recentMonths, reportHref, reportView } from "./model";

/**
 * A site's quick look at a month (`#/report/<site>/<month>`): availability as visitors saw it, the
 * interruptions (planned ones apart), serious errors, backups and maintenance. Prints as a
 * clean page to save as PDF.
 */
export function ReportPage({
  session,
  target,
  month,
  now,
}: {
  session: Session;
  target: string;
  month: string;
  now: number;
}) {
  const [report, setReport] = useState<MonthlyReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stopped = false;
    setReport(null);
    setError(null);
    (async () => {
      try {
        const token = await session.getToken();
        if (!token) throw new Error("session expired");
        const result = await api.report(token, target, month);
        if (!stopped) setReport(result);
      } catch (e) {
        if (!stopped) setError(e instanceof ApiError || e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      stopped = true;
    };
  }, [session, target, month]);

  const months = recentMonths(now, 12);
  const view = report ? reportView(report, (iso) => dayClock(iso)) : null;

  return (
    <main className="view report">
      <div className="report-bar">
        <a href={detailHref(target)}>← {target}</a>
        <label>
          <span className="sr-only">Month</span>
          <select
            value={month}
            onChange={(e) => {
              window.location.hash = reportHref(target, e.target.value).slice(1);
            }}
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m)}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="linkbtn" onClick={() => window.print()}>
          Print / save as PDF
        </button>
      </div>

      {error && <p className="muted">Couldn’t build the quick look: {error}</p>}
      {!report && !error && <p className="muted">Loading…</p>}
      {report && view && (
        <article className="report-page">
          <header>
            <h1>{view.title}</h1>
            <p className="muted">Quick look{view.coverage && ` · ${view.coverage}`}</p>
          </header>

          <section className="report-hero" data-status={view.tone}>
            <Icon name={view.tone} />
            <div>
              <b className="num">{view.availability}</b>
              <span>available to its visitors</span>
            </div>
            <div>
              <b className="num">{view.interruptions.filter((i) => !i.planned).length}</b>
              <span>
                unplanned interruptions
                {view.unplannedMinutes > 0 && `, ${view.unplannedMinutes} min in all`}
              </span>
            </div>
          </section>

          <section>
            <h2 className="eyebrow">Interruptions</h2>
            {view.interruptions.length === 0 ? (
              <p className="muted">None this month.</p>
            ) : (
              <table className="deploy-table">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Lasted</th>
                    <th scope="col">Affected</th>
                    <th scope="col">Cause</th>
                  </tr>
                </thead>
                <tbody>
                  {view.interruptions.map((i) => (
                    <tr key={i.when}>
                      <td className="num">
                        {i.when}
                        {i.planned && <span className="tag">planned</span>}
                      </td>
                      <td className="num">{i.duration}</td>
                      <td className="mono">{i.checks}</td>
                      <td>{i.cause}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h2 className="eyebrow">By check</h2>
            <table className="deploy-table">
              <thead>
                <tr>
                  <th scope="col">Check</th>
                  <th scope="col">Available</th>
                  <th scope="col">Down</th>
                </tr>
              </thead>
              <tbody>
                {view.checks.map((c) => (
                  <tr key={c.check}>
                    <td className="mono">{c.check}</td>
                    <td className="num">{c.availability}</td>
                    <td className="num">{c.down}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="report-facts">
            <h2 className="eyebrow">Also this month</h2>
            <ul>
              <li>{view.errors}.</li>
              {view.backups && <li>Backups: {view.backups}.</li>}
              {view.maintenance.length === 0 ? (
                <li>No maintenance window.</li>
              ) : (
                view.maintenance.map((m) => (
                  <li key={m.when}>
                    Maintenance {m.when}: {m.reason}
                  </li>
                ))
              )}
            </ul>
          </section>
          <p className="muted report-foot">
            Generated {dayClock(now)}. Availability counts every check of the month; an interruption
            is the time its address answered with errors or not at all.
          </p>
        </article>
      )}
    </main>
  );
}
